import { useCallback, useEffect, useState } from 'react';
import { QueryPostsDto, PostsResponse } from './useMxmPosts';
import { transformPostsToCalendarData } from '@/features/marketing/utils/dataTransformers';
import { CalendarData } from '@/features/marketing/types';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

/**
 * Convert month label (e.g., "January 2025") to YYYY-MM format
 */
const monthLabelToYYYYMM = (monthLabel: string): string => {
  const [monthName, yearStr] = monthLabel.split(' ');
  const year = parseInt(yearStr);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthIndex = monthNames.indexOf(monthName);
  if (monthIndex === -1) {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
};

/**
 * Hook to fetch posts for calendar view (alternative to calendar entries)
 * This allows the calendar to work directly from posts without requiring calendar table entries
 */
export const useMxmPostsCalendar = (
  orgId?: number,
  monthLabel?: string,
  enabled: boolean = true,
  userId?: string
) => {
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCalendar = useCallback(
    async () => {
      if ((!orgId && !userId) || !enabled || !monthLabel) return;

      setLoading(true);
      setError(null);
      try {
        const month = monthLabelToYYYYMM(monthLabel);
        const params: QueryPostsDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          month, // Use month query parameter
          suggested: true, // Get both draft and planned posts (like calendar table)
          limit: 1000, // Get all posts for the month
        };

        // Make direct API call to get posts
        const endpoint = params.userId ? '/mxm/internal/posts' : '/mxm/posts';
        const { data } = await api.get<PostsResponse>(endpoint, { params });
        const posts = data?.data || [];

        // Transform posts to calendar format
        const calendar = transformPostsToCalendarData(posts, monthLabel);
        setCalendarData(calendar);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
        console.error('[Calendar] Error fetching posts:', err);
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, monthLabel, enabled]
  );

  useEffect(() => {
    if (enabled && (orgId || userId) && monthLabel) {
      fetchCalendar();
    }
  }, [orgId, userId, monthLabel, enabled, fetchCalendar]);

  return {
    calendarData,
    loading,
    error,
    fetchCalendar,
    refetch: fetchCalendar,
  };
};

